import type { RouteData } from '../types.js';
import type { RouteProfile } from '../components/profileSelector/RouteProfileSelector.js';
import type { RouteFullData } from '../types/storage.js';

/**
 * Generate GPX XML content from route data
 */
export const generateGPXFromRouteData = (
  routeData: RouteData,
  routeName?: string,
  profile?: RouteProfile
): string => {
  const timestamp = new Date().toISOString();
  const name = routeName || `Cycling Route ${new Date().toLocaleDateString()}`;
  
  // Create GPX header
  let gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="CyclingBuddy" 
     xmlns="http://www.topografix.com/GPX/1/1"
     xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
     xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
  <metadata>
    <name>${escapeXml(name)}</name>
    <desc>Generated by CyclingBuddy</desc>
    <time>${timestamp}</time>
  </metadata>
`;

  // Add track
  gpx += `  <trk>
    <name>${escapeXml(name)}</name>
    <desc>Bike Profile: ${profile || 'Unknown'}</desc>
    <trkseg>
`;

  // Add track points with elevation data
  routeData.coordinates.forEach((coord, index) => {
    const elevation = routeData.elevationData?.[index]?.elevation || 0;
    gpx += `      <trkpt lat="${coord.lat}" lon="${coord.lng}">
        <ele>${elevation.toFixed(1)}</ele>
        <time>${timestamp}</time>
      </trkpt>
`;
  });

  gpx += `    </trkseg>
  </trk>
</gpx>`;

  return gpx;
};

/**
 * Generate GPX XML content from full route data (more complete with BRouter properties)
 */
export const generateGPXFromFullData = (
  fullData: RouteFullData,
  routeName?: string
): string => {
  const timestamp = fullData.routing_timestamp?.toISOString() || new Date().toISOString();
  const name = routeName || `Cycling Route ${new Date().toLocaleDateString()}`;
  
  // Create GPX header
  let gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="CyclingBuddy via ${fullData.routing_engine || 'BRouter'}" 
     xmlns="http://www.topografix.com/GPX/1/1"
     xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
     xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
  <metadata>
    <name>${escapeXml(name)}</name>
    <desc>Generated by CyclingBuddy using ${fullData.routing_engine || 'BRouter'}</desc>
    <time>${timestamp}</time>
    <keywords>cycling,route,${fullData.routing_profile}</keywords>
  </metadata>
`;

  // Add route statistics as extensions if available
  const properties = fullData.route_properties;
  if (properties) {
    gpx += `  <!-- Route Statistics -->
  <!-- Distance: ${properties['track-length'] ? (properties['track-length'] / 1000).toFixed(2) : 'N/A'} km -->
  <!-- Duration: ${properties['total-time'] ? Math.round(properties['total-time'] / 60) : 'N/A'} minutes -->
  <!-- Ascent: ${properties['filtered ascend'] || 'N/A'} m -->
  <!-- Profile: ${fullData.routing_profile} -->
`;
  }

  // Add track
  gpx += `  <trk>
    <name>${escapeXml(name)}</name>
    <desc>Bike Profile: ${fullData.routing_profile}</desc>
    <trkseg>
`;

  // Add track points with elevation data from the stored geometry
  if (fullData.route_geometry && fullData.route_geometry.coordinates) {
    fullData.route_geometry.coordinates.forEach((coord) => {
      const elevation = coord[2] || 0;
      gpx += `      <trkpt lat="${coord[1]}" lon="${coord[0]}">
        <ele>${elevation.toFixed(1)}</ele>
        <time>${timestamp}</time>
      </trkpt>
`;
    });
  }

  gpx += `    </trkseg>
  </trk>
</gpx>`;

  return gpx;
};

/**
 * Download GPX file from route data
 */
export const downloadGPXFromRouteData = (
  routeData: RouteData,
  routeName?: string,
  profile?: RouteProfile
): void => {
  const gpxContent = generateGPXFromRouteData(routeData, routeName, profile);
  downloadGPXFile(gpxContent, routeName);
};

/**
 * Download GPX file from full route data
 */
export const downloadGPXFromFullData = (
  fullData: RouteFullData,
  routeName?: string
): void => {
  const gpxContent = generateGPXFromFullData(fullData, routeName);
  downloadGPXFile(gpxContent, routeName);
};

/**
 * Helper function to download GPX file
 */
const downloadGPXFile = (gpxContent: string, routeName?: string): void => {

  // Create blob and download
  const blob = new Blob([gpxContent], { type: 'application/gpx+xml' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  
  // Use route name if provided, otherwise use date
  const fileName = routeName 
    ? `${routeName.replace(/[^a-zA-Z0-9-_]/g, '_')}.gpx`
    : `cycling-route-${new Date().toISOString().split('T')[0]}.gpx`;
  
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
};

/**
 * Escape XML special characters
 */
const escapeXml = (text: string): string => {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
};
